// src/pages/PagesUtilisateurs/DashboardUsers/DashboardUser.jsx

import React, { useEffect, useMemo } from "react";

import { ToastContainer } from "react-toastify";

import { MdDashboard } from "react-icons/md";

import useScrollToTop from "../../../hooks/useScollToTop";

import ScrollIndicator from "../../Home/ScrollIndicator";

import DashboardNavigation from "../../DashboardNavigation/DashboardNavigation";

import HeaderTitle from "../../../components/HeaderTitle/HeaderTitle";

import DashboardModuleNav from "./components/DashboardModuleNav";
import DashboardOverview from "./components/DashboardOverview";
import DeveloperSupportCard from "./../../../dashboard/pages/user/DashboardUsers/components/DeveloperSupportCard";
import DashboardSection from "./components/DashboardSection";

import useDashboardUser from "./hooks/useDashboardUser";
import useDashboardCounts from "./hooks/useDashboardCounts";

import { buildDashboardModules } from "./data/dashboardModules.data";

import NotificationsDisplay from "./modules/NotificationsDisplay";
import MessagesDisplay from "./modules/MessagesDisplay";
import ContactFormDisplay from "./modules/ContactFormDisplay";
import ObjectifsSaisonDisplay from "./modules/ObjectifsSaisonDisplay";
import PendingPaymentsDisplay from "./modules/PendingPaymentsDisplay";
import FavoriteTechniquesDisplay from "./modules/FavoriteTechniquesDisplay";
import FavoriteEventsDisplay from "./modules/FavoriteEventsDisplay";

/* -------------------------------------------------------------------------- */
/*                               DashboardUser                                */
/* -------------------------------------------------------------------------- */

export default function DashboardUser() {
  useScrollToTop();

  const {
    user,
    userId,
    role,
    isAdminOrTeacher,

    activeModule,
    setActiveModule,
  } = useDashboardUser();

  const { counts, updateCount } = useDashboardCounts({
    userId,
    isAdminOrTeacher,
  });

  /* ---------------------------------------------------------------------- */
  /* Modules                                                                */
  /* ---------------------------------------------------------------------- */

  const modules = useMemo(
    () =>
      buildDashboardModules({
        counts,
        isAdminOrTeacher,
      }),
    [counts, isAdminOrTeacher],
  );

  /* ---------------------------------------------------------------------- */
  /* Vérifie que le module actif existe encore                             */
  /* ---------------------------------------------------------------------- */

  useEffect(() => {
    if (!modules.length) return;

    const exists = modules.some((module) => module.id === activeModule);

    if (!exists) {
      setActiveModule("notifications");
    }
  }, [modules, activeModule, setActiveModule]);

  /* ---------------------------------------------------------------------- */
  /* Module sélectionné                                                     */
  /* ---------------------------------------------------------------------- */

  const selectedModule = useMemo(
    () => modules.find((module) => module.id === activeModule) || modules[0],
    [modules, activeModule],
  );

  /* ---------------------------------------------------------------------- */
  /* Callbacks compteurs                                                    */
  /* ---------------------------------------------------------------------- */

  const countHandlers = useMemo(
    () => ({
      notifications: (values) => updateCount("notifications", values),

      messages: (values) => updateCount("messages", values),

      contact: (values) => updateCount("contact", values),

      objectifs: (values) => updateCount("objectifs", values),

      techniques: (values) => updateCount("techniques", values),

      events: (values) => updateCount("events", values),

      payments: (values) => updateCount("payments", values),
    }),
    [updateCount],
  );

  /* ---------------------------------------------------------------------- */
  /* Render module actif                                                    */
  /* ---------------------------------------------------------------------- */

  const renderActiveModule = () => {
    switch (activeModule) {
      case "payments":
        return (
          <PendingPaymentsDisplay
            userId={userId}
            onCounts={countHandlers.payments}
          />
        );

      case "notifications":
        return (
          <NotificationsDisplay
            userId={userId}
            onCounts={countHandlers.notifications}
          />
        );

      case "messages":
        return (
          <MessagesDisplay userId={userId} onCounts={countHandlers.messages} />
        );

      case "objectifs":
        return (
          <ObjectifsSaisonDisplay
            userId={userId}
            onCounts={countHandlers.objectifs}
          />
        );

      case "events":
        return (
          <FavoriteEventsDisplay
            userId={userId}
            onCounts={countHandlers.events}
          />
        );

      case "techniques":
        return (
          <FavoriteTechniquesDisplay
            userId={userId}
            onCounts={countHandlers.techniques}
          />
        );

      case "contact":
        if (!isAdminOrTeacher) {
          return null;
        }

        return (
          <ContactFormDisplay
            userId={userId}
            onCounts={countHandlers.contact}
          />
        );

      default:
        return null;
    }
  };

  /* ---------------------------------------------------------------------- */
  /* Non connecté                                                           */
  /* ---------------------------------------------------------------------- */

  if (!userId) {
    return (
      <section
        className="
          min-h-screen
          w-full max-w-full
          overflow-x-hidden
          bg-slate-50
          dark:bg-slate-950
        "
      >
        <div className="pt-[150px]" aria-hidden />

        <div
          className="
            mx-auto
            w-full max-w-7xl
            px-3
            sm:px-6
          "
        >
          <div
            className="
              rounded-[2rem]
              border border-slate-200
              bg-white
              px-5 py-16
              text-center
              shadow-sm

              dark:border-slate-800
              dark:bg-slate-900
            "
          >
            <h1
              className="
                text-xl font-black
                text-slate-950
                dark:text-white
              "
            >
              Connexion nécessaire
            </h1>

            <p
              className="
                mt-2 text-sm
                text-slate-500
                dark:text-slate-400
              "
            >
              Vous devez être connecté pour accéder à votre tableau de bord.
            </p>
          </div>
        </div>
      </section>
    );
  }

  /* ---------------------------------------------------------------------- */
  /* Page                                                                   */
  /* ---------------------------------------------------------------------- */

  return (
    <section
      className="
        min-h-screen
        w-full max-w-full
        overflow-x-hidden
        bg-slate-50
        dark:bg-slate-950
      "
    >
      <ScrollIndicator />

      <div className="pt-[150px]" aria-hidden />

      <div
        className="
          mx-auto
          w-full max-w-7xl
          min-w-0
          px-3 pb-12
          sm:px-6
          lg:pb-16
        "
      >
        {/* -------------------------------------------------------------- */}
        {/* Header global                                                 */}
        {/* -------------------------------------------------------------- */}

        <HeaderTitle
          role="Utilisateur"
          roleId="2"
          title="Tableau de bord"
          subtitle="Un espace personnalisé pour suivre vos activités, notifications, messages, objectifs et plus encore."
          icon={MdDashboard}
          align="left"
          className="mb-4"
        />

        {/* -------------------------------------------------------------- */}
        {/* Navigation générale Dashboard                                  */}
        {/* -------------------------------------------------------------- */}

        <div
          className="
            mb-5
            w-full max-w-full
            min-w-0
            overflow-hidden
          "
        >
          <DashboardNavigation role={role} />
        </div>

        {/* -------------------------------------------------------------- */}
        {/* Résumé                                                         */}
        {/* -------------------------------------------------------------- */}

        <div
          className="
            mb-5
            w-full max-w-full
            min-w-0
          "
        >
          <DashboardOverview counts={counts} onSelect={setActiveModule} />
        </div>

        {/* -------------------------------------------------------------- */}
        {/* Soutien au développeur                                         */}
        {/* -------------------------------------------------------------- */}

        <div className="mb-5 w-full min-w-0 max-w-full">
          <DeveloperSupportCard />
        </div>

        {/* -------------------------------------------------------------- */}
        {/* Mobile : navigation modules                                    */}
        {/* -------------------------------------------------------------- */}

        <div
          className="
            mb-4
            w-full max-w-full
            min-w-0
            lg:hidden
          "
        >
          <DashboardModuleNav
            modules={modules}
            activeId={activeModule}
            onChange={setActiveModule}
          />
        </div>

        {/* -------------------------------------------------------------- */}
        {/* Desktop + contenu                                              */}
        {/* -------------------------------------------------------------- */}

        <div
          className="
            grid
            w-full max-w-full
            min-w-0
            grid-cols-1
            gap-5
            lg:grid-cols-[260px_minmax(0,1fr)]
          "
        >
          {/* Sidebar uniquement desktop */}

          <div
            className="
              hidden
              min-w-0
              lg:block
            "
          >
            <DashboardModuleNav
              modules={modules}
              activeId={activeModule}
              onChange={setActiveModule}
            />
          </div>

          {/* Contenu */}

          <main
            className="
              w-full
              max-w-full
              min-w-0
            "
          >
            {selectedModule ? (
              <DashboardSection
                title={selectedModule.title}
                subtitle={selectedModule.description}
                icon={selectedModule.icon}
                tone={selectedModule.tone}
              >
                <div
                  className="
                    w-full
                    max-w-full
                    min-w-0
                  "
                >
                  {renderActiveModule()}
                </div>
              </DashboardSection>
            ) : null}
          </main>
        </div>
      </div>

      <ToastContainer />
    </section>
  );
}
